import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import { exportTable } from "../../../utils/exportTable";
import { btn } from "../../../components/Button/buttonStyles";
import { applicationFormLink } from "../../admin/Settings/VendorPortal/vendorPortalOptions";
import ApplicationDetailModal from "./ApplicationDetailModal";
import AcceptApplicationModal from "./AcceptApplicationModal";
import {
  TYPE_STYLES,
  TypeBadge,
  StatusBadge,
  applicantName,
  applicationCode,
  formatDate,
} from "./applicationParts";

const HEADERS = [
  "Code",
  "Type",
  "Name",
  "Applicant",
  "Email",
  "Language Pairs",
  "Specializations",
  "Country",
  "Files",
  "Applied At",
  "Status",
  "Actions",
];

const TABS = [
  { value: "new", label: "New" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "", label: "All", countKey: "all" },
];

// Pending applications stand out; the other counts stay quiet.
const CountPill = ({ count, active, highlight }) => (
  <span
    className={`min-w-[20px] rounded-full px-1.5 py-px text-center text-[10px] font-semibold tabular-nums ${
      highlight
        ? "bg-brand-600 text-white"
        : active
          ? "bg-brand-50 text-brand-700"
          : "bg-gray-100 text-gray-500"
    }`}
  >
    {count}
  </span>
);

const pairLabels = (a) => a.language_pairs.map((lp) => lp.label).join(", ");
const specializationNames = (a) => a.specializations.map((s) => s.name).join(", ");

// Mirrors HEADERS above, minus Actions.
const EXPORT_COLUMNS = [
  { header: "Code", value: applicationCode },
  { header: "Type", value: (a) => a.type || "" },
  { header: "Name", value: (a) => a.company_name || "" },
  { header: "Applicant", value: applicantName },
  { header: "Email", value: (a) => a.email || "" },
  { header: "Language Pairs", value: pairLabels },
  { header: "Specializations", value: specializationNames },
  { header: "Country", value: (a) => a.country || "" },
  { header: "Files", value: (a) => a.files.length },
  { header: "Applied At", value: (a) => formatDate(a.createdAt) },
  { header: "Status", value: (a) => a.status },
];

const cell = "px-3 py-2 text-xs whitespace-nowrap text-center";

const VendorApplicationsPage = () => {
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("new");
  const [applications, setApplications] = useState([]);
  const [counts, setCounts] = useState({ new: 0, accepted: 0, rejected: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  useEffect(() => {
    api
      .get("/vendor-portal")
      .then((res) => setForm(res.data.data.settings))
      .catch(() => setForm(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/vendor-applications", { params: status ? { status } : {} });
      setApplications(res.data.data);
      if (res.data.counts) setCounts(res.data.counts);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch vendor applications");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAccept = async (payload) => {
    try {
      const res = await api.post(`/vendor-applications/${accepting.id}/accept`, payload);
      toast.success(res.data.message);
      setAccepting(null);
      setViewing(null);
      load();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || "Could not accept the application.");
      return false;
    }
  };

  const handleReject = async () => {
    try {
      const res = await api.post(`/vendor-applications/${rejecting.id}/reject`);
      toast.success(res.data.message);
      setRejecting(null);
      setViewing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not reject the application.");
      setRejecting(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Vendor Applications</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => exportTable("vendor-applications", EXPORT_COLUMNS, applications)}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {form && (
        <div className="rounded-lg border border-gray-100 bg-white px-5 py-3 text-sm text-gray-700 shadow-sm">
          {form.application_form_enabled ? (
            <>
              The vendor application form is enabled:{" "}
              <a
                href={applicationFormLink(form.application_form_token)}
                target="_blank"
                rel="noreferrer"
                className="break-all text-brand-600 hover:underline"
              >
                {applicationFormLink(form.application_form_token)}
              </a>
            </>
          ) : (
            <>
              The vendor application form is disabled, so no new applications are accepted.{" "}
              <Link to="/settings/vendor-portal" className="text-brand-600 hover:underline">
                Vendor portal settings
              </Link>
            </>
          )}
        </div>
      )}

      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4">
          <nav className="-mb-px flex gap-1" aria-label="Application status">
            {TABS.map((tab) => {
              const active = status === tab.value;
              const count = counts[tab.countKey || tab.value] ?? 0;
              return (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => setStatus(tab.value)}
                  aria-current={active ? "page" : undefined}
                  className={`flex items-center gap-2 border-b-2 px-3 py-3 text-[13px] font-medium transition-colors ${
                    active
                      ? "border-brand-600 text-brand-700"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                  <CountPill count={count} active={active} highlight={tab.value === "new" && count > 0} />
                </button>
              );
            })}
          </nav>
          {!loading && !error && (
            <p className="py-3 text-xs text-gray-500">
              Showing <span className="font-semibold text-gray-700">{applications.length}</span> items
            </p>
          )}
        </div>

        {loading && (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
          </div>
        )}
        {error && <p className="px-4 py-6 text-danger-600 text-sm">{error}</p>}

        {!loading && !error && (
          <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
            <table className="w-full table-auto border-collapse min-w-[1400px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                  {HEADERS.map((h) => (
                    <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                      No vendor applications found.
                    </td>
                  </tr>
                ) : (
                  applications.map((a, index) => {
                    const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                    return (
                      <tr
                        key={a.id}
                        className={`${rowBg} ${TYPE_STYLES[a.type] ?? ""} hover:bg-brand-50 transition-colors`}
                      >
                        <td className={cell}>
                          <button
                            type="button"
                            onClick={() => setViewing(a)}
                            className="text-brand-600 hover:text-brand-800 underline font-medium"
                          >
                            {applicationCode(a)}
                          </button>
                        </td>
                        <td className={cell}>
                          <TypeBadge type={a.type} />
                        </td>
                        <td className={`${cell} text-gray-700`}>{a.company_name || "—"}</td>
                        <td className={`${cell} text-gray-700`}>{applicantName(a) || "—"}</td>
                        <td className={`${cell} text-gray-700`}>{a.email}</td>
                        <td className={`${cell} text-gray-700 max-w-[220px] truncate`} title={pairLabels(a)}>
                          {pairLabels(a) || "—"}
                        </td>
                        <td
                          className={`${cell} text-gray-700 max-w-[180px] truncate`}
                          title={specializationNames(a)}
                        >
                          {specializationNames(a) || "—"}
                        </td>
                        <td className={`${cell} text-gray-700`}>{a.country || "—"}</td>
                        <td className={`${cell} text-gray-700`}>{a.files.length || "—"}</td>
                        <td className={`${cell} text-gray-500`}>{formatDate(a.createdAt)}</td>
                        <td className={cell}>
                          <StatusBadge status={a.status} />
                        </td>
                        <td className={cell}>
                          {a.status === "new" ? (
                            <div className="flex justify-center gap-1.5">
                              <button onClick={() => setAccepting(a)} className={btn("primary", "sm")}>
                                Accept
                              </button>
                              <button onClick={() => setRejecting(a)} className={btn("dangerSoft", "sm")}>
                                Reject
                              </button>
                            </div>
                          ) : a.status === "accepted" && a.vendor_id ? (
                            <Link
                              to={`/vendors/${a.vendor_id}`}
                              className="text-brand-600 hover:text-brand-800 underline font-medium"
                            >
                              View vendor
                            </Link>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewing && !accepting && !rejecting && (
        <ApplicationDetailModal
          application={viewing}
          canReview
          onAccept={() => setAccepting(viewing)}
          onReject={() => setRejecting(viewing)}
          onClose={() => setViewing(null)}
        />
      )}

      {accepting && (
        <AcceptApplicationModal
          application={accepting}
          onConfirm={handleAccept}
          onClose={() => setAccepting(null)}
        />
      )}

      {rejecting && (
        <ConfirmModal
          title="Reject Application"
          message={`Reject the application from ${
            rejecting.company_name || applicantName(rejecting)
          }? They will not be added as a vendor.`}
          onCancel={() => setRejecting(null)}
          onConfirm={handleReject}
          confirmText="Yes, Reject"
        />
      )}
    </div>
  );
};

export default VendorApplicationsPage;
